import { boundMethod } from 'autobind-decorator';
import type { AsyncStatus } from 'lib-shared-legacy-types';
import _isEqual from 'lodash/isEqual';
import memoize from 'memoize-one';
import { ComponentType, PureComponent } from 'react';

import type { NodeT } from 'fable/flowEditor';

import { flowsApi } from './api/flows';
import {
  FlowCreatePayloadT,
  FlowsCreateResponseT,
  FlowsGetItemResponseT,
  FlowsGetItemsResponseT,
  FlowsQueryParams,
  FlowsRemoveResponseT,
  FlowsUpdateResponseT,
  FlowT,
} from './api/types';

const { createFlow, getFlow, getFlows, removeFlow, updateFlow } = flowsApi;

export interface WithFlowsInjectedPropsT<TNode> {
  flows: {
    /**
     * Create new flow
     *
     * @param {TNode} data - Flow to be created
     */
    create: (
      data: FlowCreatePayloadT<TNode>,
    ) => Promise<FlowsCreateResponseT<TNode>>;

    /** AsyncStatus of create flow api */
    createAsyncStatus: AsyncStatus;

    /**
     * Get a single flow
     *
     * @param {string} flowId - Id of the flow to be retrieved.
     */
    getItem: (flowId: string) => Promise<FlowsGetItemResponseT<TNode>>;

    /** AsyncStatus of get flow item api */
    getItemAsyncStatus: AsyncStatus;

    /** Action to get flow items of the current page, filtered by channel */
    getItems: () => Promise<FlowsGetItemsResponseT<TNode>>;

    /** AsyncStatus of get flow items api */
    getItemsAsyncStatus: AsyncStatus;

    /** Loaded single flow items */
    item: null | FlowT<TNode>;

    /** Loaded flow items */
    items: FlowT<TNode>[];

    /**
     * Remove flow
     *
     * @param {string} flowId - Id of the flow to be removed.
     */
    remove: (flowId: string) => Promise<FlowsRemoveResponseT<TNode>>;

    /** AsyncStatus of remove flow api */
    removeAsyncStatus: AsyncStatus;

    /**
     * Update flow
     *
     * @param {string} flowId - Id of the flow to be updated.
     * @param {FlowT<TNode>} flow - Params of the flow to be updated.
     */
    update: (
      flowId: string,
      flow: FlowT<TNode>,
    ) => Promise<FlowsUpdateResponseT<TNode>>;

    /** AsyncStatus of update flow api */
    updateAsyncStatus: AsyncStatus;

    /** Change state parameters */
    paramsChange: (params: FlowsQueryParams) => Promise<void>;
  };
}

type WithFlowsNotInjectedPropsT<T, TNode> = Omit<
  T,
  keyof WithFlowsInjectedPropsT<TNode>
>;
type WithFlowsPropsT<T, TNode> = WithFlowsNotInjectedPropsT<T, TNode>;

interface WithFlowsStateT<TNode> {
  item: null | FlowT<TNode>;
  items: FlowT<TNode>[];
  params: FlowsQueryParams;
  createAsyncStatus: AsyncStatus;
  getItemAsyncStatus: AsyncStatus;
  getItemsAsyncStatus: AsyncStatus;
  removeAsyncStatus: AsyncStatus;
  updateAsyncStatus: AsyncStatus;
}

export interface WithFlowsSettings {
  initialParams?: FlowsQueryParams;
}

export const withFlows = <
  TNode extends NodeT,
  T extends WithFlowsInjectedPropsT<TNode>,
>(
  Component: ComponentType<T>,
  settings: WithFlowsSettings = {},
) => {
  class WithFlows extends PureComponent<
    WithFlowsPropsT<T, TNode>,
    WithFlowsStateT<TNode>
  > {
    constructor(props: WithFlowsPropsT<T, TNode>) {
      super(props);

      this.state = {
        item: null,
        items: [],
        params: { ...settings.initialParams },
        createAsyncStatus: 'succeeded',
        getItemAsyncStatus: 'succeeded',
        getItemsAsyncStatus: 'succeeded',
        removeAsyncStatus: 'succeeded',
        updateAsyncStatus: 'succeeded',
      };

      /** We have to bind it like this. Using @boundMethod won't work */
      this.memoizedGetInjectedProps = memoize(
        this.memoizedGetInjectedProps,
        _isEqual,
      ).bind(this);
    }

    @boundMethod
    create(
      data: FlowCreatePayloadT<TNode>,
    ): Promise<FlowsCreateResponseT<TNode>> {
      this.setState({ createAsyncStatus: 'loading' });

      return createFlow<TNode>(data)
        .then((response: FlowsCreateResponseT<TNode>) => {
          this.setState({ createAsyncStatus: 'succeeded' });
          return response;
        })
        .catch((error: Error) => {
          this.setState({ createAsyncStatus: 'failed' });
          throw error;
        });
    }

    @boundMethod
    getItem(flowId: string): Promise<FlowsGetItemResponseT<TNode>> {
      this.setState({ getItemAsyncStatus: 'loading' });

      return getFlow<TNode>(flowId)
        .then((response: FlowsGetItemResponseT<TNode>) => {
          this.setState({
            getItemAsyncStatus: 'succeeded',
            item: response.data,
          });

          return response;
        })
        .catch((error: Error) => {
          this.setState({ getItemAsyncStatus: 'failed' });
          throw error;
        });
    }

    @boundMethod
    getItems(): Promise<FlowsGetItemsResponseT<TNode>> {
      this.setState({ getItemsAsyncStatus: 'loading' });

      const params = { ...this.state.params };

      return getFlows<TNode>(params)
        .then((response: FlowsGetItemsResponseT<TNode>) => {
          const { data } = response;

          this.setState({
            getItemsAsyncStatus: 'succeeded',
            items: data.conversation_flows ?? data.flows,
          });

          return response;
        })
        .catch((error: Error) => {
          this.setState({ getItemsAsyncStatus: 'failed' });
          throw error;
        });
    }

    @boundMethod
    update(
      flowId: string,
      flow: FlowT<TNode>,
    ): Promise<FlowsUpdateResponseT<TNode>> {
      this.setState({ updateAsyncStatus: 'loading' });

      return updateFlow<TNode>(flowId, flow)
        .then((response: FlowsUpdateResponseT<TNode>) => {
          const { data: updatedItem } = response;
          /*
           * Due to backend not immediately updating the response
           * for `getItems` when requesting them after an update request,
           * we update the returned item in-state immediately after a successful update.
           */
          this.setState(({ items: stateItems }) => {
            const items = [...stateItems];
            const foundItem = items.findIndex(
              ({ id }) => id === updatedItem.id,
            );

            items[foundItem] = updatedItem;

            return { updateAsyncStatus: 'succeeded', items };
          });

          return response;
        })
        .catch((error: Error) => {
          this.setState({ updateAsyncStatus: 'failed' });
          throw error;
        });
    }

    @boundMethod
    remove(flowId: string): Promise<FlowsRemoveResponseT<TNode>> {
      this.setState({ removeAsyncStatus: 'loading' });

      return removeFlow<TNode>(flowId)
        .then((response: FlowsRemoveResponseT<TNode>) => {
          /*
           * Same issue as with `update` method,
           * the backend won't immediately update the `getItems` response,
           * so we remove it here manually upon successful response.
           */
          this.setState(({ items: stateItems }) => {
            const items = stateItems.filter(({ id }) => id !== flowId);

            return { removeAsyncStatus: 'succeeded', items };
          });

          return response;
        })
        .catch((error: Error) => {
          this.setState({ removeAsyncStatus: 'failed' });
          throw error;
        });
    }

    @boundMethod
    paramsChange(params: FlowsQueryParams) {
      return new Promise<void>((resolve) => {
        this.setState(
          ({ params: stateParams }) => ({
            params: { ...stateParams, ...params },
          }),
          () => resolve(),
        );
      });
    }

    @boundMethod
    getInjectedProps(
      _props: Readonly<WithFlowsPropsT<T, TNode>>,
      state: Readonly<WithFlowsStateT<TNode>>,
    ): WithFlowsInjectedPropsT<TNode>['flows'] {
      const {
        createAsyncStatus,
        getItemAsyncStatus,
        getItemsAsyncStatus,
        item,
        items,
        removeAsyncStatus,
        updateAsyncStatus,
      } = state;

      return this.memoizedGetInjectedProps(
        createAsyncStatus,
        getItemAsyncStatus,
        getItemsAsyncStatus,
        item,
        items,
        removeAsyncStatus,
        updateAsyncStatus,
      );
    }

    memoizedGetInjectedProps(
      createAsyncStatus: Readonly<WithFlowsStateT<TNode>>['createAsyncStatus'],
      getItemAsyncStatus: Readonly<
        WithFlowsStateT<TNode>
      >['getItemAsyncStatus'],
      getItemsAsyncStatus: Readonly<
        WithFlowsStateT<TNode>
      >['getItemsAsyncStatus'],
      item: Readonly<WithFlowsStateT<TNode>>['item'],
      items: Readonly<WithFlowsStateT<TNode>>['items'],
      removeAsyncStatus: Readonly<WithFlowsStateT<TNode>>['removeAsyncStatus'],
      updateAsyncStatus: Readonly<WithFlowsStateT<TNode>>['updateAsyncStatus'],
    ): WithFlowsInjectedPropsT<TNode>['flows'] {
      return {
        create: this.create,
        createAsyncStatus,
        getItem: this.getItem,
        getItemAsyncStatus,
        getItems: this.getItems,
        getItemsAsyncStatus,
        item,
        items,
        remove: this.remove,
        removeAsyncStatus,
        update: this.update,
        updateAsyncStatus,
        paramsChange: this.paramsChange,
      };
    }

    render() {
      return (
        <Component
          {...(this.props as T)}
          flows={this.getInjectedProps(this.props, this.state)}
        />
      );
    }
  }

  return WithFlows;
};
